import { SimpleUserRoleSchema } from "../volateq-api/api-schemas/user-schemas";
import { ApiRoles } from "../volateq-api/api-roles";
import { sortBy } from "./sort-helper";
import { UserRole } from "@/app/shared/components/app-user-role-avatar/user-role-avatar.vue";

const ROLE_IDS_BY_USER_ROLE: Record<UserRole, ApiRoles[]> = {
  f2c: [ApiRoles.SUPER_ADMIN, ApiRoles.SALESPERSON, ApiRoles.ADMIN],
  ops: [ApiRoles.SUPER_ADMIN, ApiRoles.OPERATOR, ApiRoles.ADMIN],
};

export class AnalysisRolesHelper {
  /** Resolves a selected user id to the matching Volateq user, or null when cleared/unknown. */
  static resolveUser(
    userId: string | null,
    volateqUsers: SimpleUserRoleSchema[],
  ): SimpleUserRoleSchema | null {
    if (!userId) {
      return null;
    }
    return volateqUsers.find(user => user.id === userId) ?? null;
  }

  /**
   * Selectable accounts for a given role: never locked, and restricted to the roles allowed for
   * F2C (SUPER_ADMIN, SALESPERSON, ADMIN) or Ops (SUPER_ADMIN, OPERATOR, ADMIN). Sorted alphabetically.
   */
  static candidatesForRole(users: SimpleUserRoleSchema[], role: UserRole): SimpleUserRoleSchema[] {
    const allowedRoleIds = ROLE_IDS_BY_USER_ROLE[role];
    const candidates = users.filter(
      user => !user.is_locked && user.roles.some(userRole => allowedRoleIds.includes(userRole.id)),
    );
    return sortBy(candidates, user => this.userFullName(user).toLowerCase());
  }

  /**
   * Selectable accounts for the ops-view person filter: never locked and sorted alphabetically,
   * with the current user (oneself) moved to the front.
   */
  static selectableForFilter(
    users: SimpleUserRoleSchema[],
    currentUserId: string | null,
  ): SimpleUserRoleSchema[] {
    const sorted = sortBy(users.filter(user => !user.is_locked), user => this.userFullName(user).toLowerCase());
    const self = sorted.find(user => user.id === currentUserId);
    if (!self) {
      return sorted;
    }
    return [self, ...sorted.filter(user => user.id !== currentUserId)];
  }

  static userFullName(user: { first_name: string; last_name: string; email?: string }): string {
    return `${user.first_name} ${user.last_name}`.trim() || (user.email ?? "");
  }
}
